import React, { useState, useEffect } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Truck,
  Battery,
  Clock,
  Award,
  Eye,
  Map,
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertCircle,
  Play,
  StopCircle,
  Shield,
  Wrench,
  Calendar,
  FileText,
  IdCard,
  Car,
  Gauge,
  Fuel,
  Settings,
  Activity,
  Thermometer,
  Zap,
  AlertTriangle,
  Star,
  TrendingUp,
  ChevronRight,
  X,
  Briefcase,
  Navigation2,
  Check,
  Ban,
  RefreshCw,
  Search,
  AlertCircle as AlertCircleIcon,
  Plus,
  Save,
  Loader2,
  MessageSquare,
  ThumbsUp,
  BarChart,
  TrendingDown,
  Trash2,
  LogOut,
} from "lucide-react";
import {
  driversAPI,
  vehiclesAPI,
  authAPI,
  testConnection,
} from "../services/api";

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showAssignVehicleModal, setShowAssignVehicleModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [showDriverDetails, setShowDriverDetails] = useState(false);
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showRateDriverModal, setShowRateDriverModal] = useState(false);
  const [showRemoveDriverModal, setShowRemoveDriverModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({});
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [trackingStatus, setTrackingStatus] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [addingDriver, setAddingDriver] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [removingDriver, setRemovingDriver] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [driverRatings, setDriverRatings] = useState({});
  const [ratingForm, setRatingForm] = useState({
    rating: 0,
    feedback: "",
    categories: {
      safety: 3,
      punctuality: 3,
      professionalism: 3,
      efficiency: 3,
      communication: 3,
    },
  });

  // Add driver form state
  const [newDriver, setNewDriver] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    licenseNumber: "",
    driverType: "truck",
    experience: 0,
    status: "active",
    availability: "available",
  });

  // Remove driver form state
  const [removeReason, setRemoveReason] = useState("");
  const [notes, setNotes] = useState("");

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");

      if (!token || !user) {
        setError("Please login to access the drivers page");
        setLoading(false);
        return false;
      }
      return true;
    };

    if (checkAuth()) {
      fetchDrivers();
      fetchAvailableVehicles();
    }
  }, []);

  // Test backend connection
  const testBackendConnection = async () => {
    try {
      const response = await testConnection();
      console.log("Backend connection test:", response);
    } catch (error) {
      console.error("Backend connection failed:", error);
      setError("Cannot connect to backend server. Please try again later.");
    }
  };

  // Fetch drivers data using API
  const fetchDrivers = async () => {
    try {
      setLoading(true);
      setError("");

      // Test connection first
      await testBackendConnection();

      console.log("Fetching drivers using API...");

      const response = await driversAPI.getAll();

      console.log("Drivers API Response:", response);

      if (response.success) {
        const formattedDrivers = response.data.map((driver) => ({
          ...driver,
          vehicle: driver.assignedVehicle
            ? {
                _id: driver.assignedVehicle._id || driver.assignedVehicle.id,
                registration: driver.assignedVehicle.registration,
                model: driver.assignedVehicle.model,
                make: driver.assignedVehicle.make,
                year: driver.assignedVehicle.year,
                status: driver.assignedVehicle.status,
                fuelLevel: driver.assignedVehicle.fuelLevel,
                mileage: driver.assignedVehicle.mileage,
                lastService: driver.assignedVehicle.lastService,
                nextService: driver.assignedVehicle.nextService,
              }
            : null,
          rating: typeof driver.rating === "number" ? driver.rating : 0,
        }));

        setDrivers(formattedDrivers);

        const initialTrackingStatus = {};
        formattedDrivers.forEach((driver) => {
          initialTrackingStatus[driver._id] =
            driver.trackingSettings?.autoTracking || false;
        });
        setTrackingStatus(initialTrackingStatus);

        formattedDrivers.forEach((driver) => {
          fetchDriverRatings(driver._id);
        });
      } else {
        setError(response.message || "Failed to fetch drivers");
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setError(
        error.message || "Error connecting to server. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch driver ratings using API
  const fetchDriverRatings = async (driverId) => {
    try {
      const response = await driversAPI.getRatings(driverId);

      if (response.success) {
        setDriverRatings((prev) => ({
          ...prev,
          [driverId]: response.data,
        }));
      }
    } catch (error) {
      console.error("Error fetching ratings:", error);
    }
  };

  // Handle rating submission using API
  const handleRateDriver = async (driverId) => {
    try {
      if (ratingForm.rating === 0) {
        setError("Please select a rating");
        return;
      }

      setRatingLoading(true);
      setError("");

      console.log('📤 [Rate Driver] Submitting rating:', {
        driverId,
        rating: ratingForm.rating,
        feedbackLength: ratingForm.feedback.length
      });

      const response = await driversAPI.rateDriver(driverId, ratingForm);

      console.log('✅ [Rate Driver] Response:', response);

      if (response.success) {
        const responseData = response.data || {};
        const statistics = responseData.statistics || {};
        const averageRating = statistics.averageRating || 0;
        
        setDrivers((prevDrivers) =>
          prevDrivers.map((driver) =>
            driver._id === driverId
              ? { 
                  ...driver, 
                  rating: typeof averageRating === 'number' ? averageRating : 0 
                }
              : driver
          )
        );

        const driver = drivers.find((d) => d._id === driverId);
        const driverName =
          driver?.name ||
          `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim();

        const formattedRating = typeof averageRating === 'number' 
          ? averageRating.toFixed(1) 
          : '0.0';

        setSuccessModalData({
          title: "Rating Submitted Successfully!",
          message: `Thank you for rating ${driverName}.`,
          details: `Average rating: ${formattedRating}/5.0`,
          type: "success",
        });
        setShowSuccessModal(true);

        setRatingForm({
          rating: 0,
          feedback: "",
          categories: {
            safety: 3,
            punctuality: 3,
            professionalism: 3,
            efficiency: 3,
            communication: 3,
          },
        });

        setShowRateDriverModal(false);

        fetchDriverRatings(driverId);
      } else {
        setError(response.message || "Failed to submit rating");
      }
    } catch (error) {
      console.error("❌ [Rate Driver] Error:", error);
      
      let errorMessage = "Error submitting rating. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setRatingLoading(false);
    }
  };

  // Fetch available vehicles using API
  const fetchAvailableVehicles = async () => {
    try {
      setVehicleLoading(true);

      let response;
      try {
        response = await vehiclesAPI.getAll();

        if (response.success) {
          const allVehicles = response.data;
          const assignedVehicleIds = new Set(
            drivers
              .filter((d) => d.vehicle && d.vehicle._id)
              .map((d) => d.vehicle._id)
          );

          const available = allVehicles.filter(
            (vehicle) =>
              !assignedVehicleIds.has(vehicle._id) &&
              (vehicle.status === "available" || vehicle.status === "active")
          );

          setAvailableVehicles(available);
        }
      } catch (error) {
        console.log("Trying alternative endpoint...");
        response = await driversAPI.get("/vehicles/available");
        if (response.success) {
          setAvailableVehicles(response.data);
        }
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setVehicleLoading(false);
    }
  };

  // Function to add a new driver using API
  const handleAddDriver = async () => {
    try {
      if (!newDriver.name || !newDriver.email || !newDriver.password || !newDriver.licenseNumber) {
        setError("Please fill in all required fields (Name, Email, Password, License Number)");
        return;
      }

      if (newDriver.password !== newDriver.confirmPassword) {
        setError("Passwords do not match");
        return;
      }

      if (newDriver.password.length < 6) {
        setError("Password must be at least 6 characters long");
        return;
      }

      setAddingDriver(true);
      setError("");
      setSuccessMessage("");

      const userData = JSON.parse(localStorage.getItem("user") || "{}");

      if (!userData.role) {
        setError("Please login to add a driver");
        setAddingDriver(false);
        return;
      }

      console.log("🏢 Adding driver as:", userData.role);

      let response;
      let endpoint = "";

      if (userData.role === "company_admin") {
        console.log("🏢 Using company/add-driver endpoint");
        endpoint = "/auth/company/add-driver";
        response = await authAPI.companyAddDriver(newDriver);
      } else if (userData.role === "super_admin") {
        console.log("👑 Using register-driver endpoint");
        endpoint = "/auth/register-driver";
        response = await authAPI.registerDriver(newDriver);
      } else {
        setError("You do not have permission to add drivers");
        setAddingDriver(false);
        return;
      }

      console.log("✅ Add driver response:", response);

      if (response.success) {
        setSuccessMessage(response.message || "Driver added successfully!");
        
        setNewDriver({
          name: "",
          email: "",
          password: "",
          confirmPassword: "",
          phone: "",
          licenseNumber: "",
          driverType: "truck",
          experience: 0,
          status: "active",
          availability: "available",
        });

        setShowAddDriverModal(false);

        console.log("🔄 Refreshing drivers list...");
        setTimeout(() => {
          fetchDrivers();
          fetchAvailableVehicles();
        }, 1000);
      } else {
        setError(response.message || "Failed to add driver");
      }
    } catch (error) {
      console.error("❌ Error adding driver:", error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          setError(data.message || "Validation error. Please check your input.");
        } else if (status === 403) {
          setError("You don't have permission to add drivers.");
        } else if (status === 500) {
          setError("Server error. Please try again later.");
        } else {
          setError(error.message || "Error adding driver. Please try again.");
        }
      } else {
        setError("Network error. Please check your connection and try again.");
      }
    } finally {
      setAddingDriver(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDriver((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle rating form changes
  const handleRatingFormChange = (name, value) => {
    setRatingForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle category rating changes
  const handleCategoryRatingChange = (category, value) => {
    setRatingForm((prev) => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: value,
      },
    }));
  };

  // Update driver status using API
  const updateDriverStatus = async (driverId, status) => {
    try {
      const response = await driversAPI.updateStatus(driverId, { status });

      if (response.success) {
        setDrivers((prevDrivers) =>
          prevDrivers.map((driver) =>
            driver._id === driverId ? { ...driver, status } : driver
          )
        );
        setShowStatusModal(false);
        setSelectedDriver(null);
        setSuccessModalData({
          title: "Status Updated Successfully!",
          message: `Driver status has been updated to ${status.replace(
            "_",
            " "
          )}.`,
          type: "success",
        });
        setShowSuccessModal(true);
      } else {
        setError(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setError(error.message || "Error updating status. Please try again.");
    }
  };

  // Assign vehicle to driver using API
  const assignVehicleToDriver = async (driverId, vehicleId) => {
    try {
      setAssignLoading(true);
      setError("");

      console.log("🔄 [Assign Vehicle] Starting assignment...", {
        driverId,
        vehicleId,
      });

      const cleanVehicleId = String(vehicleId).trim();
      
      if (!cleanVehicleId || cleanVehicleId.length !== 24) {
        console.error("❌ Invalid vehicle ID format:", cleanVehicleId);
        setError("Invalid vehicle ID format. Please select a valid vehicle.");
        setAssignLoading(false);
        return;
      }

      const requestBody = { 
        vehicleId: cleanVehicleId 
      };
      
      console.log("📤 [Assign Vehicle] Request body:", {
        ...requestBody,
        vehicleIdLength: cleanVehicleId.length,
        vehicleIdIsString: typeof cleanVehicleId === 'string'
      });

      const response = await driversAPI.assignVehicle(driverId, requestBody);

      console.log("✅ [Assign Vehicle] API Response:", response);

      if (response.success) {
        const assignedVehicle = availableVehicles.find(
          (v) => v._id === cleanVehicleId
        );

        if (assignedVehicle) {
          setDrivers((prevDrivers) =>
            prevDrivers.map((driver) => {
              if (driver._id === driverId) {
                return {
                  ...driver,
                  vehicle: {
                    _id: assignedVehicle._id,
                    registration: assignedVehicle.registration,
                    model: assignedVehicle.model,
                    make: assignedVehicle.make || "",
                    year: assignedVehicle.year,
                    status: "on_trip",
                    fuelLevel: assignedVehicle.fuelLevel,
                    mileage: assignedVehicle.mileage,
                  },
                  status: "on_duty",
                };
              }
              if (driver.vehicle && driver.vehicle._id === cleanVehicleId) {
                return {
                  ...driver,
                  vehicle: null,
                };
              }
              return driver;
            })
          );

          setAvailableVehicles((prev) =>
            prev.filter((v) => v._id !== cleanVehicleId)
          );
        }

        const driver = drivers.find((d) => d._id === driverId);
        const driverName =
          driver?.name ||
          `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim();
        const vehicle = assignedVehicle || availableVehicles.find((v) => v._id === cleanVehicleId);

        setSuccessModalData({
          title: "Vehicle Assigned Successfully!",
          message: `${driverName} has been assigned to vehicle ${
            vehicle?.registration || ""
          }.`,
          details: `${
            vehicle?.model || "Vehicle"
          } is now linked to ${driverName}.`,
          driverName: driverName,
          vehicleRegistration: vehicle?.registration,
          vehicleModel: vehicle?.model,
          type: "success",
        });
        setShowSuccessModal(true);

        fetchDrivers();
        fetchAvailableVehicles();
        setShowAssignVehicleModal(false);
        setSelectedDriver(null);
        setSelectedVehicleId("");
      } else {
        setError(response.message || "Failed to assign vehicle");
      }
    } catch (error) {
      console.error("❌ [Assign Vehicle] Error:", error);

      if (error.response) {
        console.error("Error details:", {
          status: error.response.status,
          data: error.response.data,
          config: error.response.config,
        });

        if (error.response.data?.message === "Invalid vehicle ID format") {
          setError(
            `Vehicle ID format error. Please check the ID: ${selectedVehicleId}`
          );
        }
      }

      setError(error.message || "Error assigning vehicle. Please try again.");
    } finally {
      setAssignLoading(false);
    }
  };

  // Unassign vehicle from driver using API
  const unassignVehicleFromDriver = async (driverId) => {
    try {
      const driver = drivers.find((d) => d._id === driverId);
      if (!driver || !driver.vehicle) {
        setError("No vehicle assigned to this driver");
        return;
      }

      const response = await driversAPI.unassignVehicle(driverId);

      if (response.success) {
        const driverName =
          driver.name ||
          `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
        const vehicleRegistration = driver.vehicle.registration;

        setSuccessModalData({
          title: "Vehicle Unassigned Successfully!",
          message: `Vehicle ${vehicleRegistration} has been unassigned from ${driverName}.`,
          details: `${driverName} is now available for new vehicle assignment.`,
          type: "success",
        });
        setShowSuccessModal(true);

        setDrivers((prevDrivers) =>
          prevDrivers.map((driver) =>
            driver._id === driverId
              ? {
                  ...driver,
                  vehicle: null,
                  status: "active",
                }
              : driver
          )
        );

        fetchAvailableVehicles();
      } else {
        setError(response.message || "Failed to unassign vehicle");
      }
    } catch (error) {
      console.error("Error unassigning vehicle:", error);
      setError(error.message || "Error unassigning vehicle. Please try again.");
    }
  };

  // Remove driver from company (send back to talent hub)
  const removeDriverFromCompany = async (driverId, reason, notes) => {
    try {
      setRemovingDriver(true);
      setError("");

      console.log("🔄 [Remove Driver] Removing driver from company...", {
        driverId,
        reason,
        notes,
      });

      // Using the backend endpoint POST /drivers/:id/remove-from-company
      const response = await driversAPI.post(`/drivers/${driverId}/remove-from-company`, {
        reason,
        notes,
      });

      console.log("✅ [Remove Driver] API Response:", response);

      if (response.success) {
        const driver = drivers.find((d) => d._id === driverId);
        const driverName =
          driver?.name ||
          `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim();

        setSuccessModalData({
          title: "Driver Removed Successfully!",
          message: `${driverName} has been removed from your company.`,
          details: `The driver has been returned to the talent hub and is available for hire by other companies.`,
          type: "success",
        });
        setShowSuccessModal(true);

        // Remove driver from local state
        setDrivers((prevDrivers) =>
          prevDrivers.filter((driver) => driver._id !== driverId)
        );

        // If driver had a vehicle, make it available
        if (driver?.vehicle) {
          fetchAvailableVehicles();
        }

        // Reset form and close modal
        setRemoveReason("");
        setNotes("");
        setShowRemoveDriverModal(false);
        setSelectedDriver(null);
      } else {
        setError(response.message || "Failed to remove driver");
      }
    } catch (error) {
      console.error("❌ [Remove Driver] Error:", error);
      
      let errorMessage = "Error removing driver. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setRemovingDriver(false);
    }
  };

  // Delete driver permanently
  const deleteDriverPermanently = async (driverId) => {
    try {
      if (!window.confirm("Are you sure you want to permanently delete this driver? This action cannot be undone.")) {
        return;
      }

      setRemovingDriver(true);
      setError("");

      console.log("🔄 [Delete Driver] Deleting driver permanently...", {
        driverId,
      });

      // Using the backend endpoint DELETE /drivers/:id
      const response = await driversAPI.delete(driverId);

      console.log("✅ [Delete Driver] API Response:", response);

      if (response.success) {
        const driver = drivers.find((d) => d._id === driverId);
        const driverName =
          driver?.name ||
          `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim();

        setSuccessModalData({
          title: "Driver Deleted Successfully!",
          message: `${driverName} has been permanently deleted from the system.`,
          details: `All driver data has been removed.`,
          type: "success",
        });
        setShowSuccessModal(true);

        // Remove driver from local state
        setDrivers((prevDrivers) =>
          prevDrivers.filter((driver) => driver._id !== driverId)
        );

        // If driver had a vehicle, make it available
        if (driver?.vehicle) {
          fetchAvailableVehicles();
        }

        setSelectedDriver(null);
      } else {
        setError(response.message || "Failed to delete driver");
      }
    } catch (error) {
      console.error("❌ [Delete Driver] Error:", error);
      
      let errorMessage = "Error deleting driver. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setRemovingDriver(false);
    }
  };

  // Start tracking using API
  const startTracking = async (driverId, vehicleId) => {
    try {
      const response = await driversAPI.startTracking(driverId, {
        vehicleId,
      });

      if (response.success) {
        setTrackingStatus((prev) => ({ ...prev, [driverId]: true }));
        setShowTrackingModal(false);

        const driver = drivers.find((d) => d._id === driverId);
        const driverName =
          driver?.name ||
          `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim();

        setSuccessModalData({
          title: "Tracking Started Successfully!",
          message: `Real-time tracking has been activated for ${driverName}.`,
          details:
            "You can now monitor the driver's location and route in real-time.",
          type: "success",
        });
        setShowSuccessModal(true);
      } else {
        setError(response.message || "Failed to start tracking");
      }
    } catch (error) {
      console.error("Error starting tracking:", error);
      setError(error.message || "Error starting tracking. Please try again.");
    }
  };

  // Stop tracking using API
  const stopTracking = async (driverId) => {
    try {
      const response = await driversAPI.stopTracking(driverId);

      if (response.success) {
        setTrackingStatus((prev) => ({ ...prev, [driverId]: false }));

        const driver = drivers.find((d) => d._id === driverId);
        const driverName =
          driver?.name ||
          `${driver?.firstName || ""} ${driver?.lastName || ""}`.trim();

        setSuccessModalData({
          title: "Tracking Stopped Successfully!",
          message: `Real-time tracking has been deactivated for ${driverName}.`,
          details: "The driver is no longer being tracked in real-time.",
          type: "info",
        });
        setShowSuccessModal(true);
      } else {
        setError(response.message || "Failed to stop tracking");
      }
    } catch (error) {
      console.error("Error stopping tracking:", error);
      setError(error.message || "Error stopping tracking. Please try again.");
    }
  };

  // Helper functions
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "on_duty":
      case "on-route":
        return "bg-green-100 text-green-800";
      case "meeting":
      case "on_break":
        return "bg-yellow-100 text-yellow-800";
      case "off_duty":
      case "off-duty":
      case "inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getVehicleStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
      case "active":
        return "bg-green-100 text-green-800";
      case "in_use":
      case "on_trip":
        return "bg-blue-100 text-blue-800";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800";
      case "out_of_service":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getFatigueColor = (percentage) => {
    if (percentage >= 70) return "bg-red-500";
    if (percentage >= 40) return "bg-yellow-500";
    return "bg-green-500";
  };

  const getFatigueTextColor = (percentage) => {
    if (percentage >= 70) return "text-red-600";
    if (percentage >= 40) return "text-yellow-600";
    return "text-green-600";
  };

  const renderStars = (
    rating,
    size = 20,
    interactive = false,
    onRatingChange = null
  ) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? "button" : "div"}
            onClick={() =>
              interactive && onRatingChange && onRatingChange(star)
            }
            className="p-0.5"
            disabled={!interactive}
          >
            <Star
              size={size}
              className={`${
                star <= rating
                  ? "text-yellow-500 fill-yellow-500"
                  : "text-gray-300"
              } ${
                interactive
                  ? "hover:scale-110 transition-transform cursor-pointer"
                  : "cursor-default"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const filteredDrivers = drivers.filter((driver) => {
    const driverName =
      driver.name ||
      `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
    const driverEmail = driver.email || driver.user?.email || "";
    const driverPhone =
      driver.phone || driver.user?.phone || driver.user?.profile?.phone || "";
    const licenseNumber = driver.licenseNumber || "";

    const matchesSearch =
      driverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driverEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driverPhone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      licenseNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filterStatus === "all" ||
      driver.status?.toLowerCase() === filterStatus.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  // Render Remove Driver Modal
  const renderRemoveDriverModal = () => {
    if (!selectedDriver || !showRemoveDriverModal) return null;

    const driverId = selectedDriver._id;
    const driverName =
      selectedDriver.name ||
      `${selectedDriver.firstName || ""} ${
        selectedDriver.lastName || ""
      }`.trim();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-xl w-full max-w-md transform transition-all duration-300 scale-100">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-800">
                  Remove Driver
                </h3>
                <p className="text-gray-600">{driverName}</p>
              </div>
              <button
                onClick={() => {
                  setShowRemoveDriverModal(false);
                  setRemoveReason("");
                  setNotes("");
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
                disabled={removingDriver}
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-yellow-700 mb-2">
                  <AlertTriangle size={20} />
                  <span className="font-medium">Important Note</span>
                </div>
                <p className="text-sm text-yellow-700">
                  Removing a driver will return them to the talent hub where they can be hired by other companies. This does not delete their account permanently.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Removal *
                </label>
                <select
                  value={removeReason}
                  onChange={(e) => setRemoveReason(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  disabled={removingDriver}
                >
                  <option value="">Select a reason</option>
                  <option value="contract_ended">Contract Ended</option>
                  <option value="performance_issues">Performance Issues</option>
                  <option value="mutual_agreement">Mutual Agreement</option>
                  <option value="company_restructuring">Company Restructuring</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  placeholder="Add any additional notes about the removal..."
                  maxLength={500}
                  disabled={removingDriver}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>Maximum 500 characters</span>
                  <span>{notes.length}/500</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => removeDriverFromCompany(driverId, removeReason, notes)}
                disabled={!removeReason || removingDriver}
                className="w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {removingDriver ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <LogOut size={20} />
                    Remove from Company
                  </>
                )}
              </button>

              <button
                onClick={() => deleteDriverPermanently(driverId)}
                disabled={removingDriver}
                className="w-full py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors font-semibold flex items-center justify-center gap-2"
              >
                <Trash2 size={20} />
                Delete Permanently
              </button>

              <button
                onClick={() => {
                  setShowRemoveDriverModal(false);
                  setRemoveReason("");
                  setNotes("");
                }}
                className="w-full py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={removingDriver}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Success Modal
  const renderSuccessModal = () => {
    if (!showSuccessModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-xl w-full max-w-md transform transition-all duration-300 scale-100">
          <div className="p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              {successModalData.type === "success" ? (
                <CheckCircle className="h-10 w-10 text-green-600" />
              ) : (
                <CheckCircle className="h-10 w-10 text-blue-600" />
              )}
            </div>

            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {successModalData.title}
            </h3>

            <p className="text-gray-600 mb-4">{successModalData.message}</p>

            {successModalData.details && (
              <p className="text-gray-500 text-sm mb-6">
                {successModalData.details}
              </p>
            )}

            {successModalData.driverName &&
              successModalData.vehicleRegistration && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#e28743] rounded-full flex items-center justify-center">
                        <User size={20} className="text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm text-gray-500">Driver</p>
                        <p className="font-semibold">
                          {successModalData.driverName}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-2xl text-gray-300">
                      <ChevronRight />
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Vehicle</p>
                        <p className="font-semibold">
                          {successModalData.vehicleRegistration}
                        </p>
                        {successModalData.vehicleModel && (
                          <p className="text-xs text-gray-500">
                            {successModalData.vehicleModel}
                          </p>
                        )}
                      </div>
                      <div className="w-10 h-10 bg-[#304a3f] rounded-full flex items-center justify-center">
                        <Truck size={20} className="text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 mt-2">
                    <div className="h-1 w-12 bg-[#e28743] rounded-full"></div>
                    <CheckCircle size={16} className="text-green-500" />
                    <div className="h-1 w-12 bg-[#304a3f] rounded-full"></div>
                  </div>
                </div>
              )}

            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors font-semibold"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Rate Driver Modal
  const renderRateDriverModal = () => {
    if (!selectedDriver || !showRateDriverModal) return null;

    const driverId = selectedDriver._id;
    const driverName =
      selectedDriver.name ||
      `${selectedDriver.firstName || ""} ${
        selectedDriver.lastName || ""
      }`.trim();
    const driverRatingsData = driverRatings[driverId] || {};

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Rate Driver</h2>
              <p className="text-gray-600">{driverName}</p>
            </div>
            <button
              onClick={() => {
                setShowRateDriverModal(false);
                setRatingForm({
                  rating: 0,
                  feedback: "",
                  categories: {
                    safety: 3,
                    punctuality: 3,
                    professionalism: 3,
                    efficiency: 3,
                    communication: 3,
                  },
                });
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
              disabled={ratingLoading}
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            {driverRatingsData.driver && (
              <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      Current Rating
                    </h3>
                    <p className="text-gray-600">
                      Based on {driverRatingsData.driver.totalRatings || 0}{" "}
                      reviews
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-4xl font-bold text-blue-600">
                      {driverRatingsData.driver.averageRating?.toFixed(1) ||
                        "0.0"}
                      <span className="text-2xl text-gray-500">/5</span>
                    </div>
                    <div className="mt-2">
                      {renderStars(
                        driverRatingsData.driver.averageRating || 0,
                        20,
                        false
                      )}
                    </div>
                  </div>
                </div>

                {driverRatingsData.driver.categoryAverages && (
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">
                      Category Breakdown
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(
                        driverRatingsData.driver.categoryAverages
                      ).map(([category, score]) => (
                        <div
                          key={category}
                          className="flex items-center justify-between"
                        >
                          <span className="text-sm text-gray-600 capitalize">
                            {category}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full">
                              <div
                                className="h-2 bg-blue-500 rounded-full"
                                style={{ width: `${(score / 5) * 100}%` }}
                              />
                            </div>
                            <span className="text-sm font-semibold w-8 text-right">
                              {score.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Overall Rating *
                </label>
                <div className="flex flex-col items-center mb-4">
                  <div className="text-5xl font-bold text-yellow-600 mb-2">
                    {ratingForm.rating.toFixed(1)}
                    <span className="text-3xl text-gray-500">/5</span>
                  </div>
                  {renderStars(ratingForm.rating, 32, true, (rating) =>
                    handleRatingFormChange("rating", rating)
                  )}
                </div>
                <div className="flex justify-between text-sm text-gray-500 px-1">
                  <span>Poor</span>
                  <span>Fair</span>
                  <span>Good</span>
                  <span>Very Good</span>
                  <span>Excellent</span>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">Category Ratings</h3>
                <div className="space-y-6">
                  {Object.entries(ratingForm.categories).map(
                    ([category, score]) => (
                      <div key={category} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-700 capitalize">
                            {category}
                          </span>
                          <span className="text-sm font-semibold">
                            {score}/5
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() =>
                                handleCategoryRatingChange(category, star)
                              }
                              className={`flex-1 py-2 text-center rounded-lg transition-all ${
                                star <= score
                                  ? "bg-blue-100 text-blue-700 border border-blue-300"
                                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                              }`}
                              disabled={ratingLoading}
                            >
                              {star}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Feedback (Optional)
                </label>
                <textarea
                  value={ratingForm.feedback}
                  onChange={(e) =>
                    handleRatingFormChange("feedback", e.target.value)
                  }
                  className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  placeholder="Share your experience working with this driver..."
                  maxLength={500}
                  disabled={ratingLoading}
                />
                <div className="flex justify-between text-sm text-gray-500 mt-1">
                  <span>Maximum 500 characters</span>
                  <span>{ratingForm.feedback.length}/500</span>
                </div>
              </div>

              {driverRatingsData.ratings &&
                driverRatingsData.ratings.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">
                      Recent Reviews
                    </h3>
                    <div className="space-y-4 max-h-48 overflow-y-auto">
                      {driverRatingsData.ratings
                        .slice(0, 3)
                        .map((rating, index) => (
                          <div
                            key={index}
                            className="bg-white border rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <User size={16} className="text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">
                                    {rating.reviewedBy?.name || "Anonymous"}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(
                                      rating.createdAt
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                {renderStars(rating.rating, 16, false)}
                              </div>
                            </div>
                            {rating.feedback && (
                              <p className="text-sm text-gray-600 mt-2">
                                {rating.feedback}
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="mt-8 pt-6 border-t flex justify-end gap-3">
              <button
                className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                onClick={() => {
                  setShowRateDriverModal(false);
                  setRatingForm({
                    rating: 0,
                    feedback: "",
                    categories: {
                      safety: 3,
                      punctuality: 3,
                      professionalism: 3,
                      efficiency: 3,
                      communication: 3,
                    },
                  });
                }}
                disabled={ratingLoading}
              >
                Cancel
              </button>
              <button
                className="px-6 py-3 bg-gradient-to-r from-[#e28743] to-orange-500 text-white rounded-lg hover:from-[#d47834] hover:to-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={() => handleRateDriver(driverId)}
                disabled={ratingLoading || ratingForm.rating === 0}
              >
                {ratingLoading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Star size={20} />
                    Submit Rating
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Add Driver Modal
  const renderAddDriverModal = () => {
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    const isCompanyAdmin = userData.role === "company_admin";
    const isSuperAdmin = userData.role === "super_admin";

    if (!isCompanyAdmin && !isSuperAdmin) {
      return null;
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Add New Driver
              </h2>
              <p className="text-gray-600">
                {isCompanyAdmin
                  ? "Add a new driver to your company"
                  : "Register a new driver in the system"}
              </p>
            </div>
            <button
              onClick={() => {
                setShowAddDriverModal(false);
                setError("");
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
              disabled={addingDriver}
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={newDriver.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  placeholder="Enter driver's full name"
                  disabled={addingDriver}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={newDriver.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  placeholder="Enter driver's email address"
                  disabled={addingDriver}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={newDriver.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  placeholder="Enter driver's phone number"
                  disabled={addingDriver}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Number *
                </label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={newDriver.licenseNumber}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  placeholder="Enter driver's license number"
                  disabled={addingDriver}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver Type
                  </label>
                  <select
                    name="driverType"
                    value={newDriver.driverType}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    disabled={addingDriver}
                  >
                    <option value="truck">Truck Driver</option>
                    <option value="bus">Bus Driver</option>
                    <option value="van">Van Driver</option>
                    <option value="car">Car Driver</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience (Years)
                  </label>
                  <input
                    type="number"
                    name="experience"
                    value={newDriver.experience}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    placeholder="Years of experience"
                    disabled={addingDriver}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={newDriver.password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    placeholder="Enter password (min 6 chars)"
                    disabled={addingDriver}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={newDriver.confirmPassword}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    placeholder="Confirm password"
                    disabled={addingDriver}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={newDriver.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    disabled={addingDriver}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Availability
                  </label>
                  <select
                    name="availability"
                    value={newDriver.availability}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    disabled={addingDriver}
                  >
                    <option value="available">Available</option>
                    <option value="busy">Busy</option>
                    <option value="unavailable">Unavailable</option>
                  </select>
                </div>
              </div>

              {isCompanyAdmin && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Note:</strong> This driver will be automatically
                    added to your company. You can assign a vehicle to them
                    after registration.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-8 pt-6 border-t flex justify-end gap-3">
              <button
                className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                onClick={() => {
                  setShowAddDriverModal(false);
                  setError("");
                }}
                disabled={addingDriver}
              >
                Cancel
              </button>
              <button
                className="px-6 py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={handleAddDriver}
                disabled={addingDriver}
              >
                {addingDriver ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Adding Driver...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Add Driver
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Assign Vehicle Modal
  const renderAssignVehicleModal = () => {
    if (!selectedDriver) return null;

    const driverId = selectedDriver._id;
    const driverName =
      selectedDriver.name ||
      `${selectedDriver.firstName || ""} ${
        selectedDriver.lastName || ""
      }`.trim();
    const assignedVehicleId = selectedDriver.vehicle?._id;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Assign Vehicle to Driver
              </h2>
              <p className="text-gray-600">{driverName}</p>
              <p className="text-sm text-gray-500">
                Driver ID: {selectedDriver.driverId || driverId}
              </p>
            </div>
            <button
              onClick={() => {
                setShowAssignVehicleModal(false);
                setSelectedVehicleId("");
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            {selectedDriver.vehicle && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <h3 className="font-semibold text-blue-800 mb-2">
                  Currently Assigned Vehicle
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold">
                      {selectedDriver.vehicle.registration}
                    </p>
                    <p className="text-gray-600">
                      {selectedDriver.vehicle.model}
                    </p>
                    <span
                      className={`px-2 py-1 text-xs rounded ${getVehicleStatusColor(
                        selectedDriver.vehicle.status
                      )}`}
                    >
                      {selectedDriver.vehicle.status || "Active"}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to unassign this vehicle?"
                        )
                      ) {
                        unassignVehicleFromDriver(driverId);
                        setShowAssignVehicleModal(false);
                      }
                    }}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-2"
                  >
                    <Ban size={16} />
                    Unassign
                  </button>
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold mb-4">Select a Vehicle</h3>

              {vehicleLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e28743] mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading vehicles...</p>
                </div>
              ) : availableVehicles.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <Truck size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No available vehicles</p>
                  <p className="text-sm text-gray-500 mt-2">
                    All vehicles are currently assigned to drivers
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {availableVehicles.map((vehicle) => {
                    const vehicleId = vehicle._id;
                    const isCurrentlyAssigned = assignedVehicleId === vehicleId;

                    return (
                      <div
                        key={vehicleId}
                        className={`p-4 border rounded-xl cursor-pointer transition-all ${
                          selectedVehicleId === vehicleId
                            ? "border-[#e28743] bg-orange-50"
                            : isCurrentlyAssigned
                            ? "border-green-500 bg-green-50"
                            : "border-gray-200 hover:border-[#e28743] hover:bg-orange-50"
                        }`}
                        onClick={() => setSelectedVehicleId(vehicleId)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-xl font-bold">
                                {vehicle.registration}
                              </p>
                              {isCurrentlyAssigned && (
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded flex items-center gap-1">
                                  <Check size={12} />
                                  Currently Assigned
                                </span>
                              )}
                            </div>
                            <p className="text-gray-700 font-medium">
                              {vehicle.model} • {vehicle.year || "N/A"}
                            </p>
                            <p className="text-sm text-gray-500">
                              {vehicle.vehicleType || "Vehicle"} •{" "}
                              {vehicle.specifications?.fuelType || "Diesel"}
                            </p>
                          </div>
                          <span
                            className={`px-2 py-1 text-xs rounded ${getVehicleStatusColor(
                              vehicle.status
                            )}`}
                          >
                            {vehicle.status || "Unknown"}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex items-center gap-1">
                            <Gauge size={14} className="text-gray-400" />
                            <span>
                              {(vehicle.mileage || 0).toLocaleString()} km
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Fuel size={14} className="text-gray-400" />
                            <span>{vehicle.fuelLevel || 0}% fuel</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-8 pt-6 border-t flex justify-end gap-3">
              <button
                className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => {
                  setShowAssignVehicleModal(false);
                  setSelectedVehicleId("");
                }}
              >
                Cancel
              </button>
              <button
                className="px-6 py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={() =>
                  assignVehicleToDriver(driverId, selectedVehicleId)
                }
                disabled={
                  !selectedVehicleId ||
                  assignLoading ||
                  selectedDriver.vehicle?._id === selectedVehicleId
                }
              >
                {assignLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Assigning...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    {selectedDriver.vehicle?._id === selectedVehicleId
                      ? "Already Assigned"
                      : "Assign Selected Vehicle"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Driver Details Modal
  const renderDriverDetails = () => {
    if (!selectedDriver) return null;

    const driverId = selectedDriver._id;
    const driverName =
      selectedDriver.name ||
      `${selectedDriver.firstName || ""} ${
        selectedDriver.lastName || ""
      }`.trim();
    const driverRatingsData = driverRatings[driverId] || {};

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{driverName}</h2>
              <p className="text-gray-600">
                {selectedDriver.driverId || driverId}
              </p>
            </div>
            <button
              onClick={() => setShowDriverDetails(false)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User size={20} className="text-[#e28743]" />
                    Personal Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500">Full Name</label>
                      <p className="font-medium">{driverName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Driver ID</label>
                      <p className="font-medium">
                        {selectedDriver.driverId || driverId}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-gray-400" />
                        <p className="font-medium">
                          {selectedDriver.email || selectedDriver.user?.email}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Phone</label>
                      <div className="flex items-center gap-2">
                        <Phone size={16} className="text-gray-400" />
                        <p className="font-medium">
                          {selectedDriver.phone ||
                            selectedDriver.user?.phone ||
                            selectedDriver.user?.profile?.phone}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">
                        License Number
                      </label>
                      <div className="flex items-center gap-2">
                        <IdCard size={16} className="text-gray-400" />
                        <p className="font-medium">
                          {selectedDriver.licenseNumber}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Status</label>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          selectedDriver.status
                        )}`}
                      >
                        {selectedDriver.status
                          ?.replace("_", " ")
                          .toUpperCase() || "UNKNOWN"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Star size={20} className="text-yellow-500" />
                      Driver Rating
                    </h3>
                    <button
                      onClick={() => {
                        setShowDriverDetails(false);
                        setSelectedDriver(selectedDriver);
                        setShowRateDriverModal(true);
                      }}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm flex items-center gap-2"
                    >
                      <Star size={14} />
                      Rate Driver
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-5xl font-bold text-yellow-600 mb-2">
                        {selectedDriver.rating?.toFixed(1) || "0.0"}
                        <span className="text-3xl text-gray-500">/5</span>
                      </div>
                      {renderStars(selectedDriver.rating || 0, 24, false)}
                      {driverRatingsData.driver && (
                        <p className="text-sm text-gray-500 mt-2">
                          Based on {driverRatingsData.driver.totalRatings || 0}{" "}
                          reviews
                        </p>
                      )}
                    </div>

                    {driverRatingsData.driver?.categoryAverages && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-700">
                          Category Breakdown
                        </h4>
                        {Object.entries(
                          driverRatingsData.driver.categoryAverages
                        ).map(([category, score]) => (
                          <div
                            key={category}
                            className="flex items-center justify-between"
                          >
                            <span className="text-sm text-gray-600 capitalize">
                              {category}
                            </span>
                            <div className="flex items-center gap-2">
                              <div className="w-32 h-2 bg-gray-200 rounded-full">
                                <div
                                  className="h-2 bg-yellow-500 rounded-full"
                                  style={{ width: `${(score / 5) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm font-semibold w-8 text-right">
                                {score.toFixed(1)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {driverRatingsData.ratings &&
                  driverRatingsData.ratings.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <MessageSquare size={20} className="text-[#e28743]" />
                        Recent Reviews
                      </h3>
                      <div className="space-y-4">
                        {driverRatingsData.ratings
                          .slice(0, 5)
                          .map((rating, index) => (
                            <div
                              key={index}
                              className="bg-white rounded-lg p-4 border"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="font-medium">
                                    {rating.reviewedBy?.name || "Anonymous"}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {new Date(
                                      rating.createdAt
                                    ).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  {renderStars(rating.rating, 16, false)}
                                </div>
                              </div>
                              {rating.feedback && (
                                <p className="text-sm text-gray-600 mt-2">
                                  {rating.feedback}
                                </p>
                              )}

                              {rating.categories && (
                                <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
                                  {Object.entries(rating.categories).map(
                                    ([category, score]) => (
                                      <div
                                        key={category}
                                        className="text-center"
                                      >
                                        <p className="text-xs text-gray-500 capitalize">
                                          {category.substring(0, 3)}
                                        </p>
                                        <p className="text-sm font-semibold">
                                          {score}
                                        </p>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {selectedDriver.vehicle && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Truck size={20} className="text-[#e28743]" />
                        Assigned Vehicle
                      </h3>
                      <button
                        onClick={() => {
                          setShowDriverDetails(false);
                          setSelectedDriver(selectedDriver);
                          setShowAssignVehicleModal(true);
                        }}
                        className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] text-sm"
                      >
                        Change Vehicle
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Car size={16} className="text-gray-400" />
                          <span className="text-sm font-medium">
                            Registration
                          </span>
                        </div>
                        <p className="text-xl font-bold">
                          {selectedDriver.vehicle.registration}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Settings size={16} className="text-gray-400" />
                          <span className="text-sm font-medium">Model</span>
                        </div>
                        <p className="text-xl font-bold">
                          {selectedDriver.vehicle.model}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Gauge size={16} className="text-gray-400" />
                          <span className="text-sm font-medium">Mileage</span>
                        </div>
                        <p className="text-xl font-bold">
                          {(
                            selectedDriver.vehicle.mileage || 0
                          ).toLocaleString()}{" "}
                          km
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Fuel size={16} className="text-gray-400" />
                          <span className="text-sm font-medium">
                            Fuel Level
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="h-2 rounded-full bg-green-500"
                              style={{
                                width: `${
                                  selectedDriver.vehicle.fuelLevel || 0
                                }%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-sm font-bold">
                            {selectedDriver.vehicle.fuelLevel || 0}%
                          </span>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={16} className="text-gray-400" />
                          <span className="text-sm font-medium">
                            Last Service
                          </span>
                        </div>
                        <p className="font-medium">
                          {selectedDriver.vehicle.lastService || "N/A"}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar size={16} className="text-gray-400" />
                          <span className="text-sm font-medium">
                            Next Service
                          </span>
                        </div>
                        <p className="font-medium">
                          {selectedDriver.vehicle.nextService || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Activity size={20} className="text-[#e28743]" />
                    Fatigue Level
                  </h3>
                  <div className="text-center mb-4">
                    <p
                      className={`text-4xl font-bold mb-2 ${getFatigueTextColor(
                        selectedDriver.fatigueLevel || 0
                      )}`}
                    >
                      {selectedDriver.fatigueLevel || 0}%
                    </p>
                    <p className="text-sm text-gray-600">
                      Current fatigue level
                    </p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div
                      className={`h-3 rounded-full ${getFatigueColor(
                        selectedDriver.fatigueLevel || 0
                      )}`}
                      style={{ width: `${selectedDriver.fatigueLevel || 0}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Low</span>
                    <span>Medium</span>
                    <span>High</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MapPin size={20} className="text-[#e28743]" />
                    Current Location
                  </h3>
                  <div className="flex items-start gap-2 mb-4">
                    <MapPin size={16} className="text-gray-400 mt-1" />
                    <p className="text-gray-700">
                      {selectedDriver.currentLocation?.address ||
                        selectedDriver.location ||
                        "Location not available"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Clock size={16} />
                    <span>
                      Last updated: {selectedDriver.lastCheckIn || "Recently"}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    Performance Stats
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Trips</span>
                      <span className="font-bold text-[#e28743]">
                        {selectedDriver.totalTrips || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Total Hours</span>
                      <span className="font-bold text-[#e28743]">
                        {selectedDriver.totalHours || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Experience</span>
                      <span className="font-bold text-[#e28743]">
                        {selectedDriver.experience || 0} years
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                  <div className="space-y-2">
                    <button
                      className="w-full flex items-center justify-between p-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834]"
                      onClick={() => {
                        setShowDriverDetails(false);
                        setSelectedDriver(selectedDriver);
                        setShowAssignVehicleModal(true);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Truck size={16} />
                        <span>
                          {selectedDriver.vehicle
                            ? "Change Vehicle"
                            : "Assign Vehicle"}
                        </span>
                      </div>
                      <ChevronRight size={16} />
                    </button>

                    <button
                      className="w-full flex items-center justify-between p-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
                      onClick={() => {
                        setShowDriverDetails(false);
                        setSelectedDriver(selectedDriver);
                        setShowRateDriverModal(true);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Star size={16} />
                        <span>Rate Driver</span>
                      </div>
                      <ChevronRight size={16} />
                    </button>

                    <button
                      className="w-full flex items-center justify-between p-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      onClick={() => {
                        setShowDriverDetails(false);
                        setSelectedDriver(selectedDriver);
                        setShowRemoveDriverModal(true);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Trash2 size={16} />
                        <span>Remove Driver</span>
                      </div>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e28743] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading drivers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircleIcon size={20} />
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError("")}
            className="text-red-700 hover:text-red-900"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} />
            <span>{successMessage}</span>
          </div>
          <button
            onClick={() => setSuccessMessage("")}
            className="text-green-700 hover:text-green-900"
          >
            <X size={20} />
          </button>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Drivers Management
          </h1>
          <p className="text-gray-600">
            Manage, monitor, and rate drivers in your fleet
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <button
            className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] flex items-center gap-2"
            onClick={() => setShowAddDriverModal(true)}
          >
            <Plus size={18} />
            Add New Driver
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Search drivers by name, email, or license..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={20}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="on_duty">On Duty</option>
            <option value="on-route">On Route</option>
            <option value="on_break">On Break</option>
            <option value="off_duty">Off Duty</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Drivers</p>
              <p className="text-2xl font-bold">{drivers.length}</p>
            </div>
            <User className="text-[#e28743]" size={24} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Rating</p>
              <p className="text-2xl font-bold text-yellow-600">
                {drivers.length > 0
                  ? (
                      drivers.reduce((sum, d) => sum + (d.rating || 0), 0) /
                      drivers.length
                    ).toFixed(1)
                  : "0.0"}
              </p>
            </div>
            <Star className="text-yellow-500" size={24} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">With Vehicles</p>
              <p className="text-2xl font-bold text-[#304a3f]">
                {drivers.filter((d) => d.vehicle).length}
              </p>
            </div>
            <Navigation2 className="text-[#304a3f]" size={24} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Without Vehicles</p>
              <p className="text-2xl font-bold text-red-600">
                {drivers.filter((d) => !d.vehicle).length}
              </p>
            </div>
            <XCircle className="text-red-500" size={24} />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredDrivers.map((driver) => {
          const driverId = driver._id;
          const driverName =
            driver.name ||
            `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
          const fatigueLevel = driver.fatigueLevel || 0;
          const lastCheckIn = driver.lastCheckIn || "Recently";
          const displayDriverId = driver.driverId || driverId;
          const driverRatingsData = driverRatings[driverId] || {};
          const totalRatings = driverRatingsData.driver?.totalRatings || 0;

          return (
            <div
              key={driverId}
              className="bg-white rounded-xl shadow-lg border overflow-hidden hover:shadow-xl transition-shadow duration-300"
            >
              <div className="bg-gradient-to-r from-[#e28743] to-[#d47834] p-6">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <User size={28} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        {driverName}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium text-white">
                          {displayDriverId}
                        </span>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            driver.status
                          )}`}
                        >
                          {driver.status?.replace("_", " ").toUpperCase() ||
                            "UNKNOWN"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    onClick={() => setSelectedDriver(driver)}
                  >
                    <MoreVertical size={20} className="text-white" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <Star
                          size={20}
                          className="text-yellow-500 fill-yellow-500"
                        />
                        <span className="text-2xl font-bold">
                          {driver.rating?.toFixed(1) || "0.0"}
                        </span>
                      </div>
                      {totalRatings > 0 ? (
                        <p className="text-xs text-gray-500">
                          {totalRatings} review{totalRatings !== 1 ? "s" : ""}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">No reviews yet</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedDriver(driver);
                      setShowRateDriverModal(true);
                    }}
                    className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 text-sm flex items-center gap-1"
                  >
                    <Star size={14} />
                    Rate
                  </button>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Mail size={16} className="text-[#e28743]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">
                        {driver.email || driver.user?.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Phone size={16} className="text-[#e28743]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium">
                        {driver.phone ||
                          driver.user?.phone ||
                          driver.user?.profile?.phone}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                      <MapPin size={16} className="text-[#e28743]" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Current Location</p>
                      <p className="font-medium">
                        {driver.currentLocation?.address ||
                          driver.location ||
                          "Location not available"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Truck size={18} className="text-gray-600" />
                      <span className="font-medium">Assigned Vehicle</span>
                    </div>
                    {driver.vehicle && (
                      <button
                        onClick={() => {
                          setSelectedDriver(driver);
                          setShowAssignVehicleModal(true);
                        }}
                        className="text-sm text-[#e28743] hover:text-[#d47834]"
                      >
                        Change
                      </button>
                    )}
                  </div>
                  {driver.vehicle ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold">
                          {driver.vehicle.registration}
                        </span>
                        <span
                          className={`px-2 py-1 rounded text-xs ${getVehicleStatusColor(
                            driver.vehicle.status
                          )}`}
                        >
                          {driver.vehicle.status || "Active"}
                        </span>
                      </div>
                      <p className="text-gray-600">{driver.vehicle.model}</p>
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500 mb-2">No vehicle assigned</p>
                      <button
                        className="text-sm text-[#e28743] hover:text-[#d47834] font-medium"
                        onClick={() => {
                          setSelectedDriver(driver);
                          setShowAssignVehicleModal(true);
                        }}
                      >
                        + Assign Vehicle
                      </button>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <Battery size={18} className="text-gray-600" />
                      <span className="font-medium">Fatigue Level</span>
                    </div>
                    <span
                      className={`text-lg font-bold ${getFatigueTextColor(
                        fatigueLevel
                      )}`}
                    >
                      {fatigueLevel}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full ${getFatigueColor(
                        fatigueLevel
                      )}`}
                      style={{ width: `${fatigueLevel}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Low</span>
                    <span>Medium</span>
                    <span>High</span>
                  </div>
                </div>

                <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Clock size={18} className="text-gray-600" />
                    <span className="font-medium">Last Check-in</span>
                  </div>
                  <span className="text-gray-600">{lastCheckIn}</span>
                </div>

                <div className="flex gap-3">
                  <button
                    className="flex-1 py-3 bg-[#e28743] text-white rounded-xl hover:bg-[#d47834] flex items-center justify-center gap-2 font-medium transition-colors"
                    onClick={() => {
                      setSelectedDriver(driver);
                      setShowDriverDetails(true);
                    }}
                  >
                    <Eye size={18} />
                    View Profile
                  </button>
                  <button
                    className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 font-medium transition-colors ${
                      trackingStatus[driverId]
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "bg-[#304a3f] text-white hover:bg-[#253830]"
                    }`}
                    onClick={() => {
                      if (driver.vehicle?._id) {
                        const vehicleId = driver.vehicle._id;
                        if (trackingStatus[driverId]) {
                          stopTracking(driverId);
                        } else {
                          startTracking(driverId, vehicleId);
                        }
                      } else {
                        setSelectedDriver(driver);
                        setShowTrackingModal(true);
                      }
                    }}
                  >
                    {trackingStatus[driverId] ? (
                      <>
                        <StopCircle size={18} />
                        Stop Track
                      </>
                    ) : (
                      <>
                        <Play size={18} />
                        Track
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dropdown Menu - UPDATED WITH CLOSE BUTTON */}
      {selectedDriver &&
        !showStatusModal &&
        !showAssignVehicleModal &&
        !showTrackingModal &&
        !showRateDriverModal &&
        !showRemoveDriverModal && (
          <div className="fixed right-4 top-20 mt-2 w-56 bg-white rounded-xl shadow-lg border z-50">
            <div className="p-2">
              {/* Close button added at the top */}
              <button
                className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-lg flex items-center justify-between transition-colors text-gray-500"
                onClick={() => setSelectedDriver(null)}
              >
                <span>Close Menu</span>
                <X size={16} />
              </button>
              <div className="border-t my-2"></div>
              
              <button
                className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-lg flex items-center gap-3 transition-colors"
                onClick={() => {
                  setShowStatusModal(true);
                  setSelectedStatus(selectedDriver.status);
                }}
              >
                <RefreshCw size={18} />
                <span>Change Status</span>
              </button>
              <button
                className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-lg flex items-center gap-3 transition-colors"
                onClick={() => {
                  setShowAssignVehicleModal(true);
                  setSelectedVehicleId("");
                }}
              >
                <Truck size={18} />
                <span>
                  {selectedDriver.vehicle ? "Change Vehicle" : "Assign Vehicle"}
                </span>
              </button>
              <button
                className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-lg flex items-center gap-3 transition-colors"
                onClick={() => {
                  setShowRateDriverModal(true);
                  setRatingForm({
                    rating: 0,
                    feedback: "",
                    categories: {
                      safety: 3,
                      punctuality: 3,
                      professionalism: 3,
                      efficiency: 3,
                      communication: 3,
                    },
                  });
                }}
              >
                <Star size={18} />
                <span>Rate Driver</span>
              </button>
              {selectedDriver.vehicle && (
                <button
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 rounded-lg flex items-center gap-3 transition-colors"
                  onClick={() => setShowTrackingModal(true)}
                >
                  <Map size={18} />
                  <span>
                    {trackingStatus[selectedDriver._id]
                      ? "Stop Tracking"
                      : "Start Tracking"}
                  </span>
                </button>
              )}
              <hr className="my-2" />
              <button
                className="w-full text-left px-4 py-3 hover:bg-red-50 rounded-lg flex items-center gap-3 transition-colors text-red-600"
                onClick={() => {
                  setShowRemoveDriverModal(true);
                  setRemoveReason("");
                  setNotes("");
                }}
              >
                <Trash2 size={18} />
                <span>Remove Driver</span>
              </button>
            </div>
          </div>
        )}

      {/* Status Update Modal */}
      {showStatusModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Update Driver Status</h3>
            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-6 focus:outline-none focus:ring-2 focus:ring-[#e28743]"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="on_duty">On Duty</option>
              <option value="on-route">On Route</option>
              <option value="on_break">On Break</option>
              <option value="off_duty">Off Duty</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => setShowStatusModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors"
                onClick={() =>
                  updateDriverStatus(selectedDriver._id, selectedStatus)
                }
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Vehicle Modal */}
      {showAssignVehicleModal && renderAssignVehicleModal()}

      {/* Add Driver Modal */}
      {showAddDriverModal && renderAddDriverModal()}

      {/* Rate Driver Modal */}
      {renderRateDriverModal()}

      {/* Remove Driver Modal */}
      {renderRemoveDriverModal()}

      {/* Tracking Modal */}
      {showTrackingModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {trackingStatus[selectedDriver._id]
                ? "Stop Tracking"
                : "Start Tracking"}
            </h3>
            <p className="text-gray-600 mb-6">
              {trackingStatus[selectedDriver._id]
                ? "Are you sure you want to stop tracking this driver?"
                : `Start tracking ${
                    selectedDriver.name ||
                    `${selectedDriver.firstName} ${selectedDriver.lastName}`
                  }'s location?`}
            </p>
            {!selectedDriver.vehicle && (
              <div className="mb-6 p-4 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-200">
                <p className="text-sm">
                  No vehicle assigned. Please assign a vehicle first.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => setShowTrackingModal(false)}
              >
                Cancel
              </button>
              {selectedDriver.vehicle && (
                <button
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    trackingStatus[selectedDriver._id]
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-[#304a3f] text-white hover:bg-[#253830]"
                  }`}
                  onClick={() => {
                    const driverId = selectedDriver._id;
                    const vehicleId = selectedDriver.vehicle._id;
                    if (trackingStatus[driverId]) {
                      stopTracking(driverId);
                    } else {
                      startTracking(driverId, vehicleId);
                    }
                    setShowTrackingModal(false);
                  }}
                >
                  {trackingStatus[selectedDriver._id]
                    ? "Stop Tracking"
                    : "Start Tracking"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Driver Details Modal */}
      {showDriverDetails && renderDriverDetails()}

      {/* Success Modal */}
      {renderSuccessModal()}

      {/* Empty State */}
      {filteredDrivers.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <User size={48} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No drivers found
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || filterStatus !== "all"
              ? "Try adjusting your search or filter criteria"
              : "No drivers are currently registered in the system"}
          </p>
          <button
            className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] flex items-center gap-2 mx-auto"
            onClick={() => setShowAddDriverModal(true)}
          >
            <Plus size={18} />
            Add New Driver
          </button>
        </div>
      )}
    </div>
  );
};

export default Drivers;